import { HTMLAttributes, ReactNode } from 'react';

import clsx from 'clsx';

import { sprinkles, tokens } from '../../tokens';
import { Button, ButtonProps } from '../Button';
import { Flex } from '../Flex';
import { Text } from '../Text';
import { divider, dividerWrapper } from './Divider.css';

export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerThickness = 2 | 4 | 8 | 12 | 16 | 24;

export interface DividerProps extends HTMLAttributes<HTMLDivElement> {
  /** Divider 방향 (가로/세로) */
  orientation?: DividerOrientation;
  /** Divider 두께 (px) */
  thickness?: DividerThickness;
  /** Divider 색상. Default: grayOpacity07 */
  color?: keyof typeof tokens.color;
  /** Divider 내부에 표시할 아이콘 */
  icon?: ReactNode;
  /** Divider 내부에 표시할 텍스트 */
  text?: string;
  /** Divider 내부에 표시할 버튼 props */
  buttonProps?: ButtonProps;
  position?: 'center' | 'start' | 'end';
}

export const Divider: React.FC<DividerProps> = ({
  orientation = 'horizontal',
  thickness = 2,
  color,
  icon,
  text,
  buttonProps,
  position = 'center',
  className,
  ...props
}) => {
  const hasContent = !!(icon || text || buttonProps);
  const defaultBorderColor = color ?? 'grayOpacity07';
  const defaultElementColor = color ?? 'gray900';

  if (!hasContent) {
    // 기존 Divider (icon, text, button이 없는 경우)
    return (
      <Flex
        align="center"
        justify="center"
        className={dividerWrapper({ orientation, thickness, hasContent })}
      >
        <div
          className={clsx(
            divider({ orientation }),
            sprinkles({ color: defaultBorderColor }),
            className,
          )}
          role="separator"
          aria-orientation={orientation}
          {...props}
        />
      </Flex>
    );
  }

  // icon, text, button이 있는 경우
  return (
    <Flex
      align="center"
      justify="center"
      gap={8}
      role="separator"
      aria-orientation={orientation}
      className={dividerWrapper({ orientation, thickness, hasContent })}
    >
      {position !== 'start' && (
        <div
          className={clsx(
            divider({ orientation }),
            sprinkles({ color: defaultBorderColor }),
            className,
          )}
        />
      )}

      {/* Icon */}
      {icon && (
        <Flex
          align="center"
          justify="center"
          className={sprinkles({ flexShrink: 0, color: defaultElementColor })}
        >
          {icon}
        </Flex>
      )}
      {/* Text */}
      {text && (
        <Text
          textStyle="13_400"
          color={defaultElementColor}
          className={sprinkles({ flexShrink: 0 })}
        >
          {text}
        </Text>
      )}
      {/* Button */}
      {buttonProps && <Button {...buttonProps} className={sprinkles({ flexShrink: 0 })} />}

      {position !== 'end' && (
        <div
          className={clsx(
            divider({ orientation }),
            sprinkles({ color: defaultBorderColor }),
            className,
          )}
        />
      )}
    </Flex>
  );
};
